import React, { useState, useEffect, useRef } from "react";
import { showToast } from "./ui/toast";

const BELEEF_BASE_URL = "https://address-finder.beleef.com.au/api";

const GooglePlacesAutocomplete = ({
  onSelectAddress,
  isNSWCheck = true,
  address,
  setAddress,
  placeholder = "Enter an address...",
  onBlur = () => {},
}) => {
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [source, setSource] = useState(null); // "beleef" or "google"

  // Initialize Google services
  useEffect(() => {
    if (window.google && window.google.maps) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
  }, []);

  // Check viewport position to determine dropdown direction
  useEffect(() => {
    const checkPosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        if (spaceAbove > spaceBelow && spaceBelow < 300) {
          setDropdownPosition("top");
        } else {
          setDropdownPosition("bottom");
        }
      }
    };
    checkPosition();
    window.addEventListener("resize", checkPosition);
    window.addEventListener("scroll", checkPosition);
    return () => {
      window.removeEventListener("resize", checkPosition);
      window.removeEventListener("scroll", checkPosition);
    };
  }, [showDropdown]);

  const extractUnitAndNumber = (addressStr) => {
    const match = addressStr.match(/^(\d+)([A-Za-z]+)$/);
    if (match) {
      return { number: match[1], unit: match[2] };
    }
    return null;
  };

  // ─── Beleef API: search ───
  const fetchBeleefPredictions = async (input) => {
    try {
      const res = await fetch(
        `${BELEEF_BASE_URL}/search?q=${encodeURIComponent(input)}`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("Beleef search error:", err);
      return [];
    }
  };

  // ─── Beleef API: details ───
  const fetchBeleefDetails = async (searchText) => {
    try {
      const res = await fetch(
        `${BELEEF_BASE_URL}/details?address=${encodeURIComponent(searchText)}`
      );
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error("Beleef details error:", err);
      return null;
    }
  };

  // ─── Handle Beleef selection ───
  const handleBeleefSelect = async (prediction) => {
    try {
      const details = await fetchBeleefDetails(prediction.search_text);
      if (!details) {
        showToast("error", "Failed to get address details.");
        return;
      }

      const fullAddress = details.search_text || prediction.search_text;

      // NSW check
      if (isNSWCheck && !fullAddress.includes("NSW")) {
        showToast("error", "Only NSW properties are allowed.");
        return;
      }

      // Build short address — strip state + postcode suffix
      let shortAddress = fullAddress.split("NSW")[0].trim();
      // Remove trailing comma if present
      shortAddress = shortAddress.replace(/,\s*$/, "").trim();

      const location = {
        lat: parseFloat(details.latitude),
        lng: parseFloat(details.longitude),
      };

      const suburb = details.locality_name || prediction.locality || "";
      const postcode = details.postcode || prediction.postcode || "";

      setAddress(shortAddress);
      setPredictions([]);
      setShowDropdown(false);
      setSource(null);

      if (onSelectAddress) {
        onSelectAddress(shortAddress, location, suburb, postcode);
      }
    } catch (error) {
      console.error("Error handling Beleef selection:", error);
      showToast("error", "Failed to get place details.");
    }
  };

  // ─── Google Places helpers (unchanged logic) ───
  const getPlaceDetails = async (placeId) => {
    return new Promise((resolve, reject) => {
      placesService.current.getDetails(
        {
          placeId,
          fields: ["address_components", "geometry", "formatted_address"],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(place);
          } else {
            reject(status);
          }
        }
      );
    });
  };

  const handleGoogleSelect = async (prediction) => {
    try {
      const place = await getPlaceDetails(prediction.place_id);
      if (place && place.address_components && place.geometry) {
        const addressComponents = place.address_components;

        const googleStreetNumber =
          addressComponents.find((c) => c.types.includes("street_number"))
            ?.long_name || "";

        const googleUnitInfo = googleStreetNumber
          ? extractUnitAndNumber(googleStreetNumber)
          : null;

        const streetName =
          addressComponents.find((c) => c.types.includes("route"))?.long_name ||
          "";

        const userInputParts = address.trim().split(/\s+/);
        const potentialStreetNumber = userInputParts[0];
        const userUnitInfo = potentialStreetNumber
          ? extractUnitAndNumber(potentialStreetNumber)
          : null;

        const suburb =
          addressComponents.find(
            (c) => c.types.includes("locality") && c.types.includes("political")
          )?.long_name || "";

        const postcode =
          addressComponents.find((c) => c.types.includes("postal_code"))
            ?.long_name || "";

        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        let fullAddress = place.formatted_address;

        if (isNSWCheck && !fullAddress.includes("NSW")) {
          showToast("error", "Only NSW properties are allowed.");
          return;
        }

        let shortAddress = fullAddress.split("NSW")[0].trim();

        addressComponents
          .filter((c) => c.types.includes("route"))
          .forEach((c) => {
            if (c.short_name && shortAddress.includes(c.short_name)) {
              shortAddress = shortAddress.replace(c.short_name, c.long_name);
            }
          });

        if (
          !googleStreetNumber &&
          /^\d+[a-zA-Z]?$/.test(potentialStreetNumber)
        ) {
          if (streetName && shortAddress.includes(streetName)) {
            shortAddress = shortAddress.replace(
              streetName,
              `${potentialStreetNumber} ${streetName}`
            );
          } else {
            shortAddress = `${potentialStreetNumber} ${shortAddress}`;
          }
        } else if (userUnitInfo && !googleUnitInfo) {
          const fullUserUnit = (
            userUnitInfo.number + userUnitInfo.unit
          ).toLowerCase();
          const googleNumberOnly = googleStreetNumber
            .replace(/\s+/g, "")
            .toLowerCase();
          const alreadyCovered =
            (googleNumberOnly && googleNumberOnly.includes(fullUserUnit)) ||
            shortAddress.toLowerCase().includes(fullUserUnit);

          if (!alreadyCovered) {
            if (
              userUnitInfo.number &&
              shortAddress.includes(userUnitInfo.number)
            ) {
              const streetNumIndex = shortAddress.indexOf(userUnitInfo.number);
              if (streetNumIndex !== -1) {
                shortAddress =
                  shortAddress.slice(0, streetNumIndex) +
                  userUnitInfo.number +
                  userUnitInfo.unit +
                  shortAddress.slice(
                    streetNumIndex + userUnitInfo.number.length
                  );
              }
            }
          }
        }

        shortAddress = shortAddress.replace(
          /\b(\d+)\s([A-Za-z])(?=\s+[A-Z])/g,
          "$1$2"
        );

        const location = { lat: latitude, lng: longitude };

        setAddress(shortAddress);
        setPredictions([]);
        setShowDropdown(false);
        setSource(null);

        if (onSelectAddress) {
          onSelectAddress(shortAddress, location, suburb, postcode);
        }
      } else {
        showToast("error", "Invalid place selected.");
      }
    } catch (error) {
      console.error("Error getting place details:", error);
      showToast("error", "Failed to get place details.");
    }
  };

  // ─── Google fallback fetch ───
  const fetchGooglePredictions = (input) => {
    if (!autocompleteService.current) return;

    autocompleteService.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: ["au"] },
        types: ["geocode"],
      },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results
        ) {
          setSource("google");
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  };

  // ─── Combined fetch: Beleef first → Google fallback ───
  const fetchPredictions = async (input) => {
    if (input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    // Try Beleef first
    const beleefResults = await fetchBeleefPredictions(input);

    if (beleefResults.length > 0) {
      setSource("beleef");
      setPredictions(beleefResults);
      setShowDropdown(true);
    } else {
      fetchGooglePredictions(input);
    }
  };

  // ─── Unified click handler ───
  const handlePredictionClick = (prediction) => {
    if (source === "beleef") {
      handleBeleefSelect(prediction);
    } else {
      handleGoogleSelect(prediction);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
    onBlur();
  };

  // ─── Render ───
  return (
    <div ref={containerRef} className="relative text-xs w-full">
      {showDropdown && predictions.length > 0 && (
        <div
          className={`absolute left-0 right-0 ${
            dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
          } bg-white border border-darkgray rounded shadow z-20 max-h-60 overflow-y-auto`}
        >
          {predictions.map((prediction, index) => {
            // Beleef items use search_text; Google items use description + place_id
            const label =
              source === "beleef"
                ? prediction.search_text
                : prediction.description;
            const key =
              source === "beleef" ? `beleef-${index}` : prediction.place_id;

            return (
              <div
                key={key}
                className="p-2 hover:bg-mediumgray cursor-pointer border-b border-mediumgray last:border-b-0"
                onClick={() => handlePredictionClick(prediction)}
              >
                <div className="text-start text-sm text-darkergray">
                  {label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <input
        type="text"
        value={address}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className="w-full relative flex-grow p-2 border border-mediumgray rounded outline-none focus:outline-none resize-none overflow-y-hidden min-h-[40px]"
      />
    </div>
  );
};

export default GooglePlacesAutocomplete;
